/**
 * ESS Climate Intelligence — Main Application Entry Point
 * Orchestrates all sections, scroll animations, and nav behaviour
 */

import './styles/main.css';

import { createHeroSection }             from './components/HeroSection.js';
import { createPacificMap }              from './components/PacificMap.js';
import { createRONIChart }               from './components/RONIChart.js';
import { createENSOOutlook }             from './components/ENSOOutlook.js';
import { createLatestClimateBriefing }   from './components/LatestClimateBriefing.js';
import { createRegionSelector }          from './components/RegionSelector.js';
import { createDataMethodologyModal }    from './components/DataMethodologyModal.js';

import { initInfoNotes }                 from './components/InfoNote.js';
import { getRegionFromURL }              from './data/regions.js';
import { trackPageview, trackRegionChange } from './data/analytics.js';

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function bootstrap() {
  const app = document.getElementById('app');
  if (!app) return;

  // Assemble page sections in order
  const sections = [
    createHeroSection(),
    createRegionSelector(),
    createPacificMap(),
    createRONIChart(),
    createENSOOutlook(),
    createLatestClimateBriefing(),
  ];

  sections.forEach(s => app.appendChild(s));



  // Data methodology modal (fixed floating button)
  app.appendChild(createDataMethodologyModal());

  // Initialise observers and UI behaviours
  initScrollAnimations();
  initNavBehaviour();
  initSectionProgressBar();
  initInfoNotes();

  // Visitor tracking — one pageview beacon on load, one more per deliberate
  // region switch. RegionSelector also dispatches ess:region-change once at
  // mount to sync other components to the URL's region; that one carries
  // isInitial:true and is skipped here since trackPageview already covers it.
  trackPageview({ region: getRegionFromURL() });
  window.addEventListener('ess:region-change', (e) => {
    if (e.detail.isInitial) return;
    trackRegionChange(e.detail.region.key);
  });
}

// ---------------------------------------------------------------------------
// Scroll Animations — Intersection Observer
// ---------------------------------------------------------------------------

function initScrollAnimations() {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target); // Fire once
        }
      });
    },
    { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
  );

  // Observe all elements with anim-on-scroll class (added lazily by components)
  function observeNew() {
    document.querySelectorAll('.anim-on-scroll:not(.in-view)').forEach(el => {
      observer.observe(el);
    });
  }

  // Initial pass + re-check after short delay (for dynamically inserted elements)
  observeNew();
  setTimeout(observeNew, 500);
  setTimeout(observeNew, 1500);
}

// ---------------------------------------------------------------------------
// Navigation — scroll spy + transparency toggle
// ---------------------------------------------------------------------------

function initNavBehaviour() {
  const nav = document.getElementById('siteNav');
  if (!nav) return;

  // Scroll-triggered background
  const onScroll = () => {
    nav.classList.toggle('scrolled', window.scrollY > 60);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // Smooth scroll for nav links
  nav.querySelectorAll('.site-nav__link').forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

// ---------------------------------------------------------------------------
// Section Progress Bar
// ---------------------------------------------------------------------------

function initSectionProgressBar() {
  const bar = document.createElement('div');
  bar.id = 'progressBar';
  bar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    height: 2px;
    background: linear-gradient(to right, #8dc53d, #895e3b);
    z-index: 9999;
    width: 0%;
    transition: width 0.1s linear;
    pointer-events: none;
  `;
  document.body.appendChild(bar);

  window.addEventListener('scroll', () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    bar.style.width = `${Math.min(100, pct)}%`;
  }, { passive: true });
}

// ---------------------------------------------------------------------------
// Run
// ---------------------------------------------------------------------------

bootstrap();
